import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { safeFetch } from '../utils/apiUtils';

const API = 'https://aayushrealproperties.in/nimaibackend/public/api';

/**
 * Report how long a visitor stayed on a page.
 * Uses sendBeacon (URLSearchParams → simple CORS request) so it survives
 * page unloads and tab closes without a preflight.
 */
const sendDuration = (visitId, seconds) => {
    if (!visitId || seconds < 1) return;
    const body = new URLSearchParams({
        visit_id: String(visitId),
        duration: String(Math.round(seconds)),
    });
    try {
        if (navigator.sendBeacon) {
            navigator.sendBeacon(`${API}/track-duration`, body);
        } else {
            fetch(`${API}/track-duration`, { method: 'POST', body, keepalive: true });
        }
    } catch {
        /* ignore */
    }
};

const TrafficTracker = () => {
    const location = useLocation();
    const current = useRef({ visitId: null, start: 0 });

    // Flush the current page's dwell time when the tab is hidden or closed.
    useEffect(() => {
        const flush = () => {
            const { visitId, start } = current.current;
            if (visitId && start) sendDuration(visitId, (Date.now() - start) / 1000);
        };
        const onVisibility = () => {
            if (document.visibilityState === 'hidden') flush();
        };
        window.addEventListener('pagehide', flush);
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            window.removeEventListener('pagehide', flush);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, []);

    useEffect(() => {
        // Don't track admin pages.
        if (location.pathname.startsWith('/admin')) return;

        let cancelled = false;
        const trackVisit = async () => {
            try {
                const { data } = await safeFetch(`${API}/track-visit`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                    body: JSON.stringify({
                        path: location.pathname,
                        referrer: document.referrer || 'direct',
                    }),
                });
                if (!cancelled) {
                    current.current = { visitId: data?.visit_id ?? null, start: Date.now() };
                }
            } catch (error) {
                console.error('Traffic tracking failed:', error);
            }
        };
        trackVisit();

        // On leaving this route, report how long the visitor stayed.
        return () => {
            cancelled = true;
            const { visitId, start } = current.current;
            if (visitId && start) sendDuration(visitId, (Date.now() - start) / 1000);
            current.current = { visitId: null, start: 0 };
        };
    }, [location.pathname]);

    return null;
};

export default TrafficTracker;
